import { forkJoin, of, Subscription } from 'rxjs';
import { Component, OnInit, OnDestroy } from "@angular/core";
import { FormGroup, FormBuilder, Validators } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { LoaderService } from "../../../common/services/loader.service";
import { ContactService } from "../../services/contacts.service";

import { DynamicDialogService } from "../../../core/services/dynamic-dialog.service";
import { Dialog, DialogButton } from "../../../core/models/dialog.model";

import { GroupService } from "../../services/group.service";
import { PermissionsService } from "../../services/permissions.service";
import { SubHeaderService } from "../../../core/services/sub-header.service";

import { Claim } from "../../models/claim.model";
import { Contact } from "../../models/contact.model";
import { genId } from "../../../core/services/genId";
import { Group } from "../../models/group.model";
import { EnumeratedPermission, PermissionsEnumerationResult } from "../../models/enumerated.permission.model";
import { SubHeader } from "../../../core/models/sub-header.model";

@Component({
    selector: "group-details",
    templateUrl: "./group-details.component.html",
    styleUrls: ['./group-details.component.scss'],
    standalone: false
})
export class GroupDetailsComponent implements OnInit, OnDestroy {

  public allPermissions: PermissionsEnumerationResult = new PermissionsEnumerationResult();
  public allUsers: Contact[];
  public currentGroup: Group;
  public dashboardClaims: Claim[] = [];
  public groupDetailsForm: FormGroup;
  public initialUserSet: Contact[] = [];
  public isEditingGroupName: boolean = false;
  public subHeader: SubHeader = new SubHeader({
    title: "Manage Groups",
    buttons: [{ name: "Cancel", method: () => this.cancel() }, { name: "Save", method: () => this.save() }]
  });
  public systemClaims: Claim[] = [];
  public widgetClaims: Claim[] = [];
  public workflowClaims: Claim[] = [];

  get hasGroupNameError(): boolean {
    if (this.groupDetailsForm) {
      return this.groupDetailsForm.controls['nameInput'].invalid && (this.groupDetailsForm.controls['nameInput'].dirty || this.groupDetailsForm.controls['nameInput'].touched);
    } else {
      return false;
    }
  }
  get currentGroupName(): string {
    if (this.currentGroup.displayName == "") {
      return "New Group"
    } else {
      return this.currentGroup.displayName;
    }
    }

    private readonly confirmationMessage: string = "Group [XXX] saved";
    confirmationDialog: Dialog = new Dialog("Group [XXX] saved.", [new DialogButton("OK", true, () => { this.onDialogConfirm(); })]);
    subscription: Subscription = new Subscription();

  constructor(
    public readonly groupService: GroupService,
    public readonly permissionsService: PermissionsService,
    public readonly contactService: ContactService,
    public readonly activatedRoute: ActivatedRoute,
    public readonly router: Router,
    public readonly formBuilder: FormBuilder,
    public subHeaderService: SubHeaderService,
    public dialogService: DynamicDialogService,
    private loader: LoaderService
  ) { }

  ngOnInit(): void {
    this.subHeaderService.updateSubHeader(this.subHeader);

    const idParam = this.activatedRoute.snapshot.params["id"];
    let group = idParam 
      ? this.groupService.getGroupById(idParam)
      : (() => {
          let g = new Group();
          g.id = genId();
          //by default, new groups are internal (bug 18537)
          g.groupType = "internal";
          return of(g);
    })();

    let permissions = this.permissionsService.get();
    let users = this.contactService.getAll<Contact[]>({ stubs: true });

    forkJoin([group, permissions, users], (group: Group, permissions: PermissionsEnumerationResult, users: Contact[]) => 
      { return { group: group, permissions: permissions, users: users }; }).subscribe(r => {
      this.currentGroup = r.group;
      this.initialUserSet = Array.from(r.group.users);
      this.allPermissions = r.permissions;
      this.allUsers = r.users;
      this.constructForm();
      this.groupDetailsForm.controls.nameInput.setValue(r.group.displayName);
      this.cleanPermissionData();
      this.setExistingPermissions();
    });
    }

    ngOnDestroy(): void {
        if (this.subscription != null) this.subscription.unsubscribe();
    }

  public cleanPermissionData(): void {
    Object.keys(this.allPermissions).forEach((permissionSet: string) => {
      for (let i = 0; i < (this.allPermissions as any)[permissionSet].length; i++) {
        const permission: EnumeratedPermission = (this.allPermissions as any)[permissionSet][i];
        if (!permission.display || !permission.type || !permission.value) {
          console.log('Warning -- filtering bad data found in ' + permissionSet + ':');
          console.log(permission);
          (this.allPermissions as any)[permissionSet].splice(i, 1);
          i--;
        }
      };
    });
  }

  public cancel(): void {
      this.router.navigate(["/admin/manage-groups"]);
  }

  public constructForm(): void {
    this.groupDetailsForm = this.formBuilder.group({
      nameInput: ["", Validators.required],
      nonGroupPermissionsSelect: [""],
      groupPermissionsSelect: [""],
      nonGroupUsersSelect: [""],
      groupUsersSelect: [""]
    });
  }

    public save() {
        if (this.groupDetailsForm.valid) {
            this.currentGroup.permissions = Array.from(this.workflowClaims).concat(this.dashboardClaims).concat(this.widgetClaims).concat(this.systemClaims);
            this.loader.toggleLoader(true);
            this.subscription = this.groupService.save(this.currentGroup).subscribe(
                (data) => {                
                    this.loader.toggleLoader(false);
                    this.confirmationDialog.message = this.confirmationMessage.replace("[XXX]", data.displayName);
                    this.confirmationDialog.toggleDialog(true);
                    this.dialogService.updateDialog(this.confirmationDialog);                
                }, (error) => {
                    this.loader.toggleLoader(false);
                    console.error(error);
                    this.dialogService.showError();
                });
        } else {
            //force form validation
            Object.keys(this.groupDetailsForm.controls).forEach(field => {
                const control = this.groupDetailsForm.get(field);
                control?.markAsTouched({ onlySelf: true });
            });
        }
    }

  public updateDashboardClaims(claims: Claim[]) {
    this.dashboardClaims = claims;
  }

  public updateGroupType(entry: string): void {
    this.currentGroup.groupType = entry;
  }

  public updateSystemClaims(claims: Claim[]) {
    this.systemClaims = claims;
  }

  public updateUsers(contacts: Contact[]) {
    this.currentGroup.users = contacts;
  }

  public updateWidgetClaims(claims: Claim[]) {
    this.widgetClaims = claims;
  }

  public updateWorkflowClaims(claims: Claim[]) {
    this.workflowClaims = claims;
  }

  public isMatchingPermission(all: any, existing: any): boolean {
    return existing.type === all.type && existing.value === all.value;
  }

  public setExistingPermissions(): void {
    this.allPermissions.dashboards.forEach(p => {
      const found = this.currentGroup.permissions.find(c => this.isMatchingPermission(p, c));
      if (found) this.dashboardClaims.push(found);
    });

    this.allPermissions.permissions.forEach(p => {
      const found = this.currentGroup.permissions.find(c => this.isMatchingPermission(p, c));
      if (found) this.systemClaims.push(found);
    });

    this.allPermissions.widgets.forEach(p => {
      const found = this.currentGroup.permissions.find(c => this.isMatchingPermission(p, c));
      if (found) this.widgetClaims.push(found);
    });

    this.allPermissions.workflows.forEach(p => {
      const found = this.currentGroup.permissions.find(c => this.isMatchingPermission(p, c));
      if (found) this.workflowClaims.push(found);
    });
  }

    public onDialogConfirm(): void {
        this.router.navigate(["/admin/manage-groups"]);
    }



}
